import { emptySplitApi } from "./emptyApi"
import type {
  Campaign,
  CreateCampaignDto,
  UpdateCampaignDto,
  UpdateCampaignBudgetDto,
  UpdateCampaignStatusDto,
  AdSet,
  CreateAdSetDto,
  UpdateAdSetDto,
  UpdateAdSetBudgetDto,
  UpdateAdSetTargetingDto,
  UpdateAdSetStatusDto,
  Ad,
  CreateAdDto,
  UpdateAdDto,
  UpdateAdStatusDto,
  LaunchAdDto,
  SupportedPlatform,
  ApplyUrlTagsRequest,
  ApplyUrlTagsResponse,
  ApplyExclusionsRequest,
  ApplyExclusionsResponse,
  CreativeTestEligibility,
  CreativeTestView,
} from "./types/paid-media.types"

/** GET /cli/google-ads/scale-suggestion — tROAS to run for a desired daily spend. */
export interface GoogleAdsScaleSuggestion {
  supported: boolean
  reason?: string
  kind?: "targetRoas"
  /** Applying needs portfolioOverride — the UI keeps these rows budget-only. */
  portfolio?: boolean
  currentTargetRoas?: number
  suggestedTargetRoas?: number
  source?: "simulation" | "heuristic"
  clampedBy?: string[]
  /**
   * False when a guardrail capped the move short of the requested spend — the
   * target moves toward the goal but will not reach it, so the UI must not
   * imply the new budget will be spent in full.
   */
  reachesDesiredSpend?: boolean
  /** Learning/cooldown — the target cannot move right now. */
  blockers?: string[]
}

/** POST /cli/google-ads/preview — single-use token for the confirm step. */
export interface GoogleAdsPreviewResponse {
  token: string
  expiresAt: string
  changeType: string
  entityId: string
  preview: {
    before: Record<string, unknown>
    after: Record<string, unknown>
    warnings: string[]
  }
}

export const paidMediaApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    // Platform endpoints
    getSupportedPlatforms: builder.query<string[], void>({
      query: () => "paid-media/platforms",
      providesTags: ["PaidMediaPlatforms"],
    }),

    // Campaign endpoints
    getCampaigns: builder.query<
      Campaign[],
      { platform: SupportedPlatform; adAccountId?: string | null }
    >({
      query: ({ platform, adAccountId }) => ({
        url: `paid-media/${platform}/campaigns`,
        params: adAccountId ? { adAccountId } : undefined,
      }),
      providesTags: (result, _error, { platform }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Campaign" as const, id })),
              { type: "Campaign", id: "LIST", platform },
            ]
          : [{ type: "Campaign", id: "LIST", platform }],
    }),

    getCampaign: builder.query<
      Campaign,
      { platform: SupportedPlatform; campaignId: string }
    >({
      query: ({ platform, campaignId }) =>
        `paid-media/${platform}/campaigns/${campaignId}`,
      providesTags: (_result, _error, { campaignId }) => [
        { type: "Campaign", id: campaignId },
      ],
    }),

    createCampaign: builder.mutation<
      Campaign,
      { platform: SupportedPlatform; data: CreateCampaignDto }
    >({
      query: ({ platform, data }) => ({
        url: `paid-media/${platform}/campaigns`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { platform }) => [
        { type: "Campaign", id: "LIST", platform },
      ],
    }),

    updateCampaign: builder.mutation<
      Campaign,
      {
        platform: SupportedPlatform
        campaignId: string
        data: UpdateCampaignDto
      }
    >({
      query: ({ platform, campaignId, data }) => ({
        url: `paid-media/${platform}/campaigns/${campaignId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { campaignId, platform }) => [
        { type: "Campaign", id: campaignId },
        { type: "Campaign", id: "LIST", platform },
      ],
    }),

    deleteCampaign: builder.mutation<
      void,
      { platform: SupportedPlatform; campaignId: string }
    >({
      query: ({ platform, campaignId }) => ({
        url: `paid-media/${platform}/campaigns/${campaignId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { campaignId, platform }) => [
        { type: "Campaign", id: campaignId },
        { type: "Campaign", id: "LIST", platform },
      ],
    }),

    updateCampaignBudget: builder.mutation<
      Campaign,
      {
        platform: SupportedPlatform
        campaignId: string
        data: UpdateCampaignBudgetDto
        /** Skip the per-entity `budget-update` audit row — bulk flows audit the batch themselves. */
        skipRecord?: boolean
      }
    >({
      query: ({ platform, campaignId, data, skipRecord }) => ({
        url: `paid-media/${platform}/campaigns/${campaignId}/budget${skipRecord ? "?skipRecord=true" : ""}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: "Campaign", id: campaignId },
      ],
    }),

    updateCampaignStatus: builder.mutation<
      Campaign,
      {
        platform: SupportedPlatform
        campaignId: string
        data: UpdateCampaignStatusDto
      }
    >({
      query: ({ platform, campaignId, data }) => ({
        url: `paid-media/${platform}/campaigns/${campaignId}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: "Campaign", id: campaignId },
      ],
    }),

    // Google Ads smart-scale endpoints (/cli/google-ads/* — session-auth'd,
    // company resolved server-side from the slug/id we pass).
    getGoogleAdsScaleSuggestion: builder.query<
      GoogleAdsScaleSuggestion,
      {
        company: string
        campaign: string
        currentDailySpendCents: number
        desiredDailySpendCents: number
      }
    >({
      query: ({
        company,
        campaign,
        currentDailySpendCents,
        desiredDailySpendCents,
      }) => ({
        url: `cli/google-ads/scale-suggestion?company=${encodeURIComponent(company)}&campaign=${campaign}&currentDailySpendCents=${currentDailySpendCents}&desiredDailySpendCents=${desiredDailySpendCents}`,
      }),
    }),

    previewGoogleAdsChange: builder.mutation<
      GoogleAdsPreviewResponse,
      {
        company: string
        changeType: "campaign-budget" | "bid-target"
        entityId: string
        budgetCents?: number
        targetRoas?: number
        /** Marks the Budget Recommender's own moves in the audit so they never hold the entity against the recommender. */
        initiator?: "budget-recommender"
      }
    >({
      query: body => ({
        url: `cli/google-ads/preview`,
        method: "POST",
        body,
      }),
    }),

    confirmGoogleAdsChange: builder.mutation<
      { ok: boolean; auditId: string | null },
      { company: string; token: string }
    >({
      query: body => ({
        url: `cli/google-ads/confirm`,
        method: "POST",
        body,
      }),
      // Invalidates EVERY campaign list, not just Google's. RTK Query's
      // FullTagDescription is `{ type, id }` only, so any extra property on a
      // tag — `platform` here, `campaignId`/`adsetId` elsewhere in this file —
      // is dropped before matching. Writing `platform: "google"` would read as
      // per-platform isolation that does not exist, so it is left off rather
      // than implying a guarantee. Real isolation needs the discriminator
      // folded into `id` (e.g. `LIST-google`) at every provider AND every
      // invalidator for that type; doing it partially is worse than not doing
      // it, because a missed invalidator silently serves stale data.
      invalidatesTags: () => [{ type: "Campaign", id: "LIST" }],
    }),

    // AdSet endpoints
    searchAdSets: builder.query<
      AdSet[],
      { platform: SupportedPlatform; campaignId?: string; search?: string }
    >({
      query: ({ platform, campaignId, search }) => {
        const qs = new URLSearchParams()
        if (campaignId) qs.set("campaignId", campaignId)
        if (search?.trim()) qs.set("search", search.trim())
        const suffix = qs.toString()
        return `paid-media/${platform}/adsets/search${suffix ? `?${suffix}` : ""}`
      },
      providesTags: (result, _error, { platform }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "AdSet" as const, id })),
              { type: "AdSet", id: "SEARCH", platform },
            ]
          : [{ type: "AdSet", id: "SEARCH", platform }],
    }),

    getAdSets: builder.query<
      AdSet[],
      { platform: SupportedPlatform; campaignId: string }
    >({
      query: ({ platform, campaignId }) =>
        `paid-media/${platform}/campaigns/${campaignId}/adsets`,
      providesTags: (result, _error, { campaignId, platform }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "AdSet" as const, id })),
              { type: "AdSet", id: "LIST", campaignId, platform },
            ]
          : [{ type: "AdSet", id: "LIST", campaignId, platform }],
    }),

    getAdSet: builder.query<
      AdSet,
      { platform: SupportedPlatform; adsetId: string }
    >({
      query: ({ platform, adsetId }) =>
        `paid-media/${platform}/adsets/${adsetId}`,
      providesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    /**
     * Meta creative test pre-flight (MD-5569): live read of the ad set's
     * delivery, bid strategy, budget, active studies and 7-day spend. Read
     * whenever the blast picker's "creative test" mode is on.
     */
    getCreativeTestEligibility: builder.query<CreativeTestEligibility, string>({
      query: adSetId =>
        `paid-media/meta/adsets/${adSetId}/creative-test/eligibility`,
    }),
    /** A Meta creative test (ad study) with its cells and outcome, by study id. */
    getCreativeTest: builder.query<CreativeTestView, string>({
      query: studyId => `paid-media/meta/creative-tests/${studyId}`,
    }),

    createAdSet: builder.mutation<
      AdSet,
      { platform: SupportedPlatform; campaignId: string; data: CreateAdSetDto }
    >({
      query: ({ platform, campaignId, data }) => ({
        url: `paid-media/${platform}/campaigns/${campaignId}/adsets`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { campaignId, platform }) => [
        { type: "AdSet", id: "LIST", campaignId, platform },
      ],
    }),

    updateAdSet: builder.mutation<
      AdSet,
      { platform: SupportedPlatform; adsetId: string; data: UpdateAdSetDto }
    >({
      query: ({ platform, adsetId, data }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    deleteAdSet: builder.mutation<
      void,
      { platform: SupportedPlatform; adsetId: string }
    >({
      query: ({ platform, adsetId }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    updateAdSetBudget: builder.mutation<
      AdSet,
      {
        platform: SupportedPlatform
        adsetId: string
        data: UpdateAdSetBudgetDto
        /** Skip the per-entity `budget-update` audit row — bulk flows audit the batch themselves. */
        skipRecord?: boolean
      }
    >({
      query: ({ platform, adsetId, data, skipRecord }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}/budget${skipRecord ? "?skipRecord=true" : ""}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    updateAdSetTargeting: builder.mutation<
      AdSet,
      {
        platform: SupportedPlatform
        adsetId: string
        data: UpdateAdSetTargetingDto
      }
    >({
      query: ({ platform, adsetId, data }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}/targeting`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    updateAdSetStatus: builder.mutation<
      AdSet,
      {
        platform: SupportedPlatform
        adsetId: string
        data: UpdateAdSetStatusDto
      }
    >({
      query: ({ platform, adsetId, data }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adsetId }) => [
        { type: "AdSet", id: adsetId },
      ],
    }),

    // Ad endpoints
    getAds: builder.query<
      Ad[],
      { platform: SupportedPlatform; adsetId: string }
    >({
      query: ({ platform, adsetId }) =>
        `paid-media/${platform}/adsets/${adsetId}/ads`,
      providesTags: (result, _error, { adsetId, platform }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Ad" as const, id })),
              { type: "Ad", id: "LIST", adsetId, platform },
            ]
          : [{ type: "Ad", id: "LIST", adsetId, platform }],
    }),

    getAd: builder.query<Ad, { platform: SupportedPlatform; adId: string }>({
      query: ({ platform, adId }) => `paid-media/${platform}/ads/${adId}`,
      providesTags: (_result, _error, { adId }) => [{ type: "Ad", id: adId }],
    }),

    createAd: builder.mutation<
      Ad,
      { platform: SupportedPlatform; adsetId: string; data: CreateAdDto }
    >({
      query: ({ platform, adsetId, data }) => ({
        url: `paid-media/${platform}/adsets/${adsetId}/ads`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adsetId, platform }) => [
        { type: "Ad", id: "LIST", adsetId, platform },
      ],
    }),

    createAdsBulk: builder.mutation<
      {
        results: Array<{
          adsetId: string
          success: boolean
          ad?: Ad
          error?: string
        }>
        successCount: number
        failCount: number
      },
      {
        platform: SupportedPlatform
        items: Array<{ adsetId: string; data: CreateAdDto }>
      }
    >({
      query: ({ platform, items }) => ({
        url: `paid-media/${platform}/ads/bulk`,
        method: "POST",
        body: { items },
      }),
      invalidatesTags: (_result, _error, { platform }) => [
        { type: "Ad", id: "LIST", platform },
      ],
    }),

    updateAd: builder.mutation<
      Ad,
      { platform: SupportedPlatform; adId: string; data: UpdateAdDto }
    >({
      query: ({ platform, adId, data }) => ({
        url: `paid-media/${platform}/ads/${adId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adId }) => [
        { type: "Ad", id: adId },
      ],
    }),

    deleteAd: builder.mutation<
      void,
      { platform: SupportedPlatform; adId: string }
    >({
      query: ({ platform, adId }) => ({
        url: `paid-media/${platform}/ads/${adId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { adId }) => [
        { type: "Ad", id: adId },
      ],
    }),

    updateAdStatus: builder.mutation<
      Ad,
      { platform: SupportedPlatform; adId: string; data: UpdateAdStatusDto }
    >({
      query: ({ platform, adId, data }) => ({
        url: `paid-media/${platform}/ads/${adId}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adId }) => [
        { type: "Ad", id: adId },
      ],
    }),

    launchAd: builder.mutation<
      Ad,
      { platform: SupportedPlatform; adId: string; data: LaunchAdDto }
    >({
      query: ({ platform, adId, data }) => ({
        url: `paid-media/${platform}/ads/${adId}/launch`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { adId }) => [
        { type: "Ad", id: adId },
      ],
    }),

    blastAd: builder.mutation<
      BlastJobAccepted,
      {
        platform: SupportedPlatform
        adId: string
        data: {
          target_adsets: Record<string, string[]>
          overrides?: {
            destinationUrl?: string
            headline?: string
            description?: string
            primaryText?: string
            callToAction?: string
            adName?: string
            multiAdvertiserAds?: boolean
          }
          sourcePreference?: "object_story_id" | "creative_id" | "auto"
          objectStoryId?: string
        }
      }
    >({
      query: ({ platform, adId, data }) => ({
        url: `paid-media/${platform}/ads/${adId}/blast`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Ad", "AdSet"],
    }),

    applyUrlTags: builder.mutation<
      ApplyUrlTagsResponse,
      { platform: SupportedPlatform; data: ApplyUrlTagsRequest }
    >({
      query: ({ platform, data }) => ({
        url: `paid-media/${platform}/ads/apply-url-tags`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Ad"],
    }),

    blastPost: builder.mutation<
      BlastJobAccepted,
      {
        platform: SupportedPlatform
        postId: string
        data: {
          target_adsets: Record<string, string[]>
          overrides?: {
            destinationUrl?: string
            headline?: string
            description?: string
            primaryText?: string
            callToAction?: string
            multiAdvertiserAds?: boolean
          }
          imageUrl?: string
          caption?: string
        }
      }
    >({
      query: ({ platform, postId, data }) => ({
        url: `paid-media/${platform}/posts/${postId}/blast`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Ad", "AdSet"],
    }),

    applyExclusions: builder.mutation<
      ApplyExclusionsResponse,
      { platform: SupportedPlatform; data: ApplyExclusionsRequest }
    >({
      query: ({ platform, data }) => ({
        url: `paid-media/${platform}/adsets/apply-exclusions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AdSet"],
    }),

    /**
     * DB-backed cross-platform list of every Ad row for the company (SYNC +
     * APP origin). Drives the AdBlaster Ads tab. Joined with adSet + campaign
     * for hierarchy rendering and adCreative for the row thumbnail.
     */
    listCompanyAds: builder.query<CompanyAdRow[], void>({
      query: () => "paid-media/ads",
      providesTags: result =>
        result
          ? [
              ...result.map(a => ({ type: "Ad" as const, id: a.id })),
              { type: "Ad" as const, id: "COMPANY_LIST" },
            ]
          : [{ type: "Ad" as const, id: "COMPANY_LIST" }],
    }),

    /**
     * Live-fetch the Meta AdCreative library for the AdBlaster Creatives tab.
     * No DB persistence — each call hits Meta. First-page only (~50 rows).
     * `search` is forwarded to Meta as a server-side CONTAIN filter so we
     * surface matches across the full library, not just the first page.
     */
    listMetaAdCreatives: builder.query<
      MetaCreativeRow[],
      { adAccountId?: string; search?: string } | void
    >({
      query: arg => {
        const params: Record<string, string> = {}
        if (arg?.adAccountId) params.adAccountId = arg.adAccountId
        if (arg?.search) params.search = arg.search
        return {
          url: "paid-media/meta/creatives",
          params: Object.keys(params).length ? params : undefined,
        }
      },
      // No tag invalidation — Meta is the source of truth here, not us.
    }),

    /**
     * Live image / thumbnail URLs for Meta AdCreatives, read from Meta itself
     * (not the Asset library). The Creative Report cards use these for Meta
     * creatives. Signed fbcdn URLs that expire — keep them in memory only.
     */
    getMetaCreativeMedia: builder.mutation<
      MetaCreativeMediaResponse,
      { creativeIds: string[] }
    >({
      query: body => ({
        url: "paid-media/meta/creatives/media",
        method: "POST",
        body,
      }),
    }),

    /** getMetaCreativeMedia keyed by Meta ad id, for surfaces that only know the ad. */
    getMetaAdMedia: builder.mutation<
      MetaCreativeMediaResponse,
      { adIds: string[] }
    >({
      query: body => ({
        url: "paid-media/meta/ads/media",
        method: "POST",
        body,
      }),
    }),

    /**
     * Resolve a Page post (pageId_postId) to its permalink + published state
     * so the creative drawer only embeds posts Facebook will actually render
     * (MD-5599). `null` means the post could not be read.
     */
    getMetaPostPreview: builder.query<
      MetaPostPreview | null,
      { objectStoryId: string }
    >({
      query: ({ objectStoryId }) =>
        `paid-media/meta/posts/${objectStoryId}/preview`,
    }),

    /**
     * TikTok counterpart of listMetaAdCreatives: the advertiser's ads (TikTok
     * has no separate creative library). Empty when TikTok Ads Management is
     * not connected; callers skip it in that case anyway.
     */
    listTikTokAdCreatives: builder.query<
      TikTokCreativeRow[],
      { search?: string } | void
    >({
      query: arg => ({
        url: "paid-media/tiktok/creatives",
        params: arg?.search ? { search: arg.search } : undefined,
      }),
    }),

    /**
     * TikTok advertiser selector (company settings) — the TikTok counterpart
     * of campaignBlasterApi's Meta ad-account trio. The list is what the Ads
     * Management connector authorized; the saved id is the account every
     * TikTok write (blast, campaign, ad group) targets.
     */
    listTikTokAdvertisers: builder.query<
      Array<{ id: string; name: string }>,
      void
    >({
      query: () => ({ url: "paid-media/tiktok/advertisers" }),
    }),
    getCurrentTikTokAdvertiser: builder.query<
      { advertiserId: string | null },
      void
    >({
      query: () => ({ url: "paid-media/tiktok/advertiser" }),
      providesTags: ["TikTokAdvertiser"],
    }),
    setTikTokAdvertiser: builder.mutation<
      { advertiserId: string },
      { advertiserId: string }
    >({
      query: body => ({
        url: "paid-media/tiktok/advertiser",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TikTokAdvertiser"],
    }),

    /**
     * Blast a Meta `creative_id` directly across N target ad sets. Used by
     * AdBlaster's Creatives tab when the selected source is a Meta-library
     * row — no AdForge import step in between.
     */
    blastMetaCreative: builder.mutation<
      BlastJobAccepted,
      {
        creativeId: string
        body: { target_adsets: { meta: string[] }; adName?: string }
      }
    >({
      query: ({ creativeId, body }) => ({
        url: `paid-media/meta/creatives/${creativeId}/blast`,
        method: "POST",
        body,
      }),
      // Each successful per-ad-set call upserts an Ad row server-side, so the
      // AdBlaster Ads tab list cache needs a refresh.
      invalidatesTags: ["Ad"],
    }),

    /**
     * Gemini analysis of a live ad's creative (Meta, TikTok, or YouTube). The
     * full-quality asset is pulled fresh from the platform (the app only
     * caches thumbnails). Async: enqueues a one-ad `analyze-creative` job and
     * returns `{ jobRunId }` — videos take ~30-90s (download + Gemini file
     * processing), past the 29s API Gateway cutoff that used to surface as a
     * CORS error. Await it with `useWaitForJob`; the job result is an
     * `AnalyzeCreativeJobResult` whose `results[0]` is this ad. Pass
     * `save: true` to have the job persist the report (no second request).
     */
    analyzeAdCreative: builder.mutation<
      JobAccepted,
      {
        platform: CreativeAnalysisPlatform
        adId: string
        prompt?: string
        save?: boolean
      }
    >({
      query: ({ platform, adId, prompt, save }) => ({
        url: `paid-media/${platform}/ads/${adId}/analyze-creative`,
        method: "POST",
        body: { ...(prompt ? { prompt } : {}), ...(save ? { save } : {}) },
      }),
    }),

    /**
     * Persist an analysis the user chose to keep (analyze-creative itself
     * stores nothing). Echoes the analyze response back; the server
     * re-validates tag picks and generates the search embedding.
     */
    /**
     * Analyze an image/video by URL (e.g. an organic post's S3 copy) and get
     * taxonomy-validated tag suggestions before any ad exists. Async: enqueues
     * an `analyze-media` job (videos take up to ~90s, past the gateway
     * limit). Await it with `useWaitForJob`; the job result is an
     * `AnalyzeMediaJobResult`.
     */
    analyzeMedia: builder.mutation<
      JobAccepted,
      { url: string; mediaType: "image" | "video"; prompt?: string }
    >({
      query: body => ({
        url: "paid-media/creative-analysis/media",
        method: "POST",
        body,
      }),
    }),
    saveAdCreativeAnalysis: builder.mutation<
      { assetId: string },
      {
        platform: CreativeAnalysisPlatform
        adId: string
        body: SaveAdCreativeAnalysisBody
      }
    >({
      query: ({ platform, adId, body }) => ({
        url: `paid-media/${platform}/ads/${adId}/analysis`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { adId }) => [
        { type: "AdCreativeAnalysis", id: adId },
      ],
    }),

    /**
     * Gemini analysis of an ORGANIC social post (the post drawer's "Analyze
     * with AI"): enqueues a one-post `analyze-post-creative` job. The server
     * reads the post's synced asset copy first, then the cached URLs sent
     * here, then Graph; `save` (default true) keeps the report on the post's
     * asset row. Await with `useWaitForJob`; the result is an
     * `AnalyzePostCreativeJobResult`.
     */
    analyzePostCreative: builder.mutation<
      JobAccepted,
      {
        postId: string
        channel?: "facebook" | "instagram"
        videoUrl?: string
        imageUrl?: string
        caption?: string
      }
    >({
      query: ({ postId, ...body }) => ({
        url: `paid-media/creative-analysis/posts/${encodeURIComponent(postId)}`,
        method: "POST",
        body,
      }),
    }),

    /** The analysis saved for an organic post (null when none). */
    getPostCreativeAnalysis: builder.query<
      GetPostCreativeAnalysisResponse,
      { postId: string; channel?: "facebook" | "instagram" }
    >({
      query: ({ postId, channel }) => ({
        url: `paid-media/creative-analysis/posts/${encodeURIComponent(postId)}`,
        params: channel ? { channel } : undefined,
      }),
      providesTags: (_result, _error, { postId }) => [
        { type: "AdCreativeAnalysis", id: `post:${postId}` },
      ],
    }),

    /**
     * The analysis previously saved for this ad (null when none). Lets the
     * drawer show the stored report on open instead of forcing a re-analysis.
     */
    getAdCreativeAnalysis: builder.query<
      GetAdCreativeAnalysisResponse,
      { platform: CreativeAnalysisPlatform; adId: string }
    >({
      query: ({ platform, adId }) =>
        `paid-media/${platform}/ads/${adId}/analysis`,
      providesTags: (_result, _error, { adId }) => [
        { type: "AdCreativeAnalysis", id: adId },
      ],
    }),
  }),
})

/**
 * Platforms the creative-analysis endpoints accept. `youtube` is the YouTube
 * video behind a Google Ads video ad — send the `youtube_ads` ad id; the
 * server resolves the video from the creative report.
 */
export type CreativeAnalysisPlatform = "meta" | "tiktok" | "youtube"

/**
 * Cube `media_channel` → analyze-creative platform. `null` for channels with
 * nothing to analyze (e.g. `google_ads` search ads), which hides the button.
 */
export function toCreativeAnalysisPlatform(
  mediaChannel: string | undefined,
): CreativeAnalysisPlatform | null {
  switch (mediaChannel?.toLowerCase().trim()) {
    case "meta":
    case "facebook":
    case "instagram":
      return "meta"
    case "tiktok":
    case "tiktok_ads":
      return "tiktok"
    case "youtube":
    case "youtube_ads":
      return "youtube"
    default:
      return null
  }
}

/** POST /paid-media/:platform/ads/:adId/analysis */
export interface SaveAdCreativeAnalysisBody {
  mediaType: "video" | "image" | "text"
  adName?: string
  analysis: string
  suggestedTags?: SuggestedTag[]
  suggestedMediaCategory?: string
  prompt?: string
}

/** What a single ad's analysis looks like (one `analyze-creative` job row, or the saved copy). */
export interface AdCreativeAnalysisResult {
  adId: string
  adName: string
  mediaType: "video" | "image" | "text"
  analysis: string
  suggestedTags: SuggestedTag[]
  suggestedMediaCategory?: string
}

/** Which signal decided a suggested tag: the creative itself, or an ad-metadata field (MD-5600). */
export type SuggestedTagSource =
  | "creative"
  | "ad_name"
  | "primary_text"
  | "headline"
  | "description"
  | "link"
  | "mixed"

export const SUGGESTED_TAG_SOURCE_LABELS: Record<SuggestedTagSource, string> = {
  creative: "creative",
  ad_name: "ad name",
  primary_text: "primary text",
  headline: "headline",
  description: "description",
  link: "destination link",
  mixed: "creative + metadata",
}

export interface SuggestedTag {
  category: string
  options: string[]
  reason?: string
  source?: SuggestedTagSource
}

/** Analysis + tag picks for a media URL (no ad yet) — the `analyze-media` job result minus the summary. */
export interface MediaAnalysisResult {
  mediaType: "video" | "image"
  analysis: string
  suggestedTags: SuggestedTag[]
  suggestedMediaCategory?: string
}

/** `result` of a COMPLETED `analyze-media` job (POST /paid-media/creative-analysis/media). */
export interface AnalyzeMediaJobResult extends MediaAnalysisResult {
  summary: { total: number; successful: number; failed: number }
}

/**
 * One row of an `analyze-creative` job result. A per-ad failure keeps the
 * row and carries `error` (the job itself still COMPLETEs); a failed save
 * after a successful analysis carries the analysis plus `saveError`.
 */
export interface AnalyzeCreativeJobRow {
  adId: string
  adName?: string
  mediaType?: "video" | "image" | "text"
  analysis?: string
  suggestedTags?: SuggestedTag[]
  suggestedMediaCategory?: string
  saved: boolean
  assetId?: string
  saveError?: string
  error?: string
}

/** `result` of a COMPLETED `analyze-creative` job (POST /paid-media/:platform/ads/:adId/analyze-creative enqueues a one-row one). */
export interface AnalyzeCreativeJobResult {
  platform: CreativeAnalysisPlatform
  results: AnalyzeCreativeJobRow[]
  summary: { total: number; analyzed: number; saved: number; failed: number }
}

/** One organic post's analysis (an `analyze-post-creative` job row, or the saved copy). */
export interface PostCreativeAnalysisResult {
  postId: string
  mediaType: "video" | "image" | "text"
  analysis: string
  suggestedTags: SuggestedTag[]
  suggestedMediaCategory?: string
}

/** `result` of a COMPLETED `analyze-post-creative` job; a failed post keeps its row with `error`. */
export interface AnalyzePostCreativeJobResult {
  results: Array<
    Partial<PostCreativeAnalysisResult> & {
      postId: string
      saved?: boolean
      saveError?: string
      error?: string
    }
  >
  summary: { total: number; successful: number; failed: number }
}

/** GET /paid-media/creative-analysis/posts/:postId */
export interface GetPostCreativeAnalysisResponse {
  savedAnalysis:
    | (PostCreativeAnalysisResult & { analyzedAt: string | null })
    | null
}

/** GET /paid-media/:platform/ads/:adId/analysis */
export interface GetAdCreativeAnalysisResponse {
  savedAnalysis:
    | (AdCreativeAnalysisResult & { analyzedAt: string | null })
    | null
}

/**
 * Blast endpoints are async now — they enqueue an `ad-blast` job and return
 * immediately. The ads are created in the background; progress + completion
 * surface via the Novu "Jobs" notification feed in the header. Poll
 * GET /jobs/:id with the returned id if you need the per-ad-set result.
 */
export interface JobAccepted {
  jobRunId: string
  status: string
}
export type BlastJobAccepted = JobAccepted

export type MetaCreativeMediaResponse = Record<
  string,
  {
    imageUrl: string | null
    thumbnailUrl: string | null
    /** Playable video source, when the creative is a video Meta lets us read. */
    videoUrl: string | null
    mediaType: "IMAGE" | "VIDEO"
  }
>

export interface BlastMetaCreativeResponse {
  creativeId: string
  results: Array<{
    adSetId: string
    success: boolean
    externalAdId?: string
    error?: string
  }>
  successCount: number
  failCount: number
}

/** GET /paid-media/tiktok/creatives — one of the advertiser's ads, shaped as a creative row. */
export interface TikTokCreativeRow {
  id: string
  adGroupId: string | null
  name: string
  primaryText: string | null
  callToAction: string | null
  landingPageUrl: string | null
  status: string | null
  thumbnailUrl: string | null
  hasVideo: boolean
  /** TikTok `create_time` ("YYYY-MM-DD HH:mm:ss", UTC) — null when absent. */
  createdAt: string | null
}

/** GET paid-media/meta/posts/:objectStoryId/preview — null when the post can't be read. */
export interface MetaPostPreview {
  /** Canonical Facebook permalink of the post, when Graph returns one. */
  permalinkUrl: string | null
  /** false for unpublished ("dark") ad posts, which the embed plugin can't render. */
  isPublished: boolean | null
}

export interface MetaCreativeRow {
  id: string
  name: string | null
  title: string | null
  body: string | null
  thumbnailUrl: string | null
  objectStoryId: string | null
  effectiveObjectStoryId: string | null
  effectiveInstagramMediaId: string | null
  /** CTA enum (e.g. SHOP_NOW) — creative-level type, or the asset_feed_spec list joined with ", ". */
  callToAction: string | null
}

export interface CompanyAdRow {
  id: string
  companyId: string
  externalId: string
  platform: "META" | "TIKTOK" | "YOUTUBE" | "GOOGLE" | "LINKEDIN"
  name: string
  status:
    | "ACTIVE"
    | "PAUSED"
    | "DELETED"
    | "ARCHIVED"
    | "PENDING_REVIEW"
    | "DISAPPROVED"
  format: string | null
  origin: "APP" | "SYNC"
  adCreativeId: string | null
  metaCreativeId: string | null
  createdAt: string
  updatedAt: string
  lastSyncedAt: string | null
  adSet: {
    id: string
    externalId: string
    name: string
    campaign: { id: string; externalId: string; name: string }
  }
  adCreative: {
    id: string
    name: string
    format: string
    mediaAssets: unknown
  } | null
}

export const {
  // Platform hooks
  useGetSupportedPlatformsQuery,

  // Campaign hooks
  useGetCampaignsQuery,
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,
  useUpdateCampaignBudgetMutation,
  useUpdateCampaignStatusMutation,

  // Google Ads smart-scale hooks
  useLazyGetGoogleAdsScaleSuggestionQuery,
  usePreviewGoogleAdsChangeMutation,
  useConfirmGoogleAdsChangeMutation,

  // AdSet hooks
  useSearchAdSetsQuery,
  useGetAdSetsQuery,
  useGetAdSetQuery,
  useCreateAdSetMutation,
  useUpdateAdSetMutation,
  useDeleteAdSetMutation,
  useUpdateAdSetBudgetMutation,
  useUpdateAdSetTargetingMutation,
  useUpdateAdSetStatusMutation,

  // Meta creative test (MD-5569)
  useGetCreativeTestEligibilityQuery,
  useGetCreativeTestQuery,

  // Meta post preview (MD-5599)
  useGetMetaPostPreviewQuery,

  // Ad hooks
  useGetAdsQuery,
  useGetAdQuery,
  useCreateAdMutation,
  useCreateAdsBulkMutation,
  useUpdateAdMutation,
  useDeleteAdMutation,
  useUpdateAdStatusMutation,
  useLaunchAdMutation,
  useBlastAdMutation,

  // URL Tags hooks
  useApplyUrlTagsMutation,

  // Post hooks (organic post blasting)
  useBlastPostMutation,

  // TikTok creatives (AdBlaster Creatives tab) + advertiser selector
  useListTikTokAdCreativesQuery,
  useListTikTokAdvertisersQuery,
  useGetCurrentTikTokAdvertiserQuery,
  useSetTikTokAdvertiserMutation,

  // Exclusions
  useApplyExclusionsMutation,

  // AdBlaster — DB-backed cross-platform ads list
  useListCompanyAdsQuery,
  // AdBlaster — live-fetched Meta creative library
  useListMetaAdCreativesQuery,
  useLazyListMetaAdCreativesQuery,
  // AdBlaster — direct Meta-creative blast (no AdForge import step)
  useBlastMetaCreativeMutation,
  // Creative Report — live Meta creative images/thumbnails
  useGetMetaCreativeMediaMutation,
  useGetMetaAdMediaMutation,

  // Optimization drawer — Gemini analysis of an ad's creative (video or image)
  useAnalyzeAdCreativeMutation,
  useAnalyzeMediaMutation,
  useAnalyzePostCreativeMutation,
  useGetPostCreativeAnalysisQuery,
  useSaveAdCreativeAnalysisMutation,
  useGetAdCreativeAnalysisQuery,
} = paidMediaApi
